/**
 * API kullanıcı nesnelerinde silinmiş kullanıcı gösterimi (isDeleted) için ortak metin ve kontroller.
 */

export type UserPresenceFields = {
  isDeleted?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  role?: string | null;
  thumbnailUrl?: string | null;
};

export function isUserDeleted(user: UserPresenceFields | null | undefined): boolean {
  return user?.isDeleted === true;
}

export function getUserBaseDisplayName(
  user: UserPresenceFields | null | undefined,
  userIdFallback?: number | null
): string {
  if (!user) {
    return userIdFallback != null ? `User ${userIdFallback}` : "Unknown user";
  }
  const parts = [user.firstName, user.lastName].filter((p) => p && String(p).trim());
  if (parts.length) return parts.join(" ");
  if (user.email) return user.email;
  return userIdFallback != null ? `User ${userIdFallback}` : "Unknown user";
}

export const DELETED_USER_LABEL = "(deleted user)";

/** İsim + gerekiyorsa " (deleted user)" */
export function getUserDisplayNameWithDeleted(
  user: UserPresenceFields | null | undefined,
  userIdFallback?: number | null
): string {
  const base = getUserBaseDisplayName(user, userIdFallback);
  if (isUserDeleted(user)) {
    return `${base} ${DELETED_USER_LABEL}`;
  }
  return base;
}

/** Mention seçiminde inputa yazılacak tam metin (mapping anahtarı ile aynı olmalı) */
export function getMentionTokenForInput(user: UserPresenceFields & { firstName?: string | null }): string {
  const base = user.firstName?.trim() || user.email?.split("@")[0] || "user";
  return `@${base}`;
}

export function findShareMemberByEmail(
  email: string,
  ...pools: (readonly unknown[] | null | undefined)[]
): UserPresenceFields | undefined {
  const target = String(email || "").trim().toLowerCase();
  if (!target) return undefined;
  for (const pool of pools) {
    if (!pool?.length) continue;
    for (const raw of pool) {
      const u = raw as { email?: string | null };
      if (u?.email && String(u.email).trim().toLowerCase() === target) {
        return raw as UserPresenceFields;
      }
    }
  }
  return undefined;
}

/** Share havuzlarından silinmiş kullanıcı e-postalarını toplar */
export function collectDeletedEmailsFromSharePools(
  ...pools: (readonly unknown[] | null | undefined)[]
): Set<string> {
  const deleted = new Set<string>();
  for (const pool of pools) {
    if (!pool?.length) continue;
    for (const raw of pool) {
      const user = raw as UserPresenceFields;
      if (user?.email && isUserDeleted(user)) {
        deleted.add(String(user.email).trim().toLowerCase());
      }
    }
  }
  return deleted;
}

export function mergeDeletedEmailSets(...sets: Set<string>[]): Set<string> {
  const merged = new Set<string>();
  for (const set of sets) {
    set.forEach((email) => {
      merged.add(String(email).trim().toLowerCase());
    });
  }
  return merged;
}

/** Share öneri listesinden silinmiş kullanıcıları çıkarır */
export function filterShareSuggestionEmails(
  emails: string[],
  deletedEmails: Set<string>
): string[] {
  return emails.filter((email) => {
    const key = String(email || "").trim().toLowerCase();
    return key.length > 0 && !deletedEmails.has(key);
  });
}

/** Entry share listesinden silinmemiş assignee kayıtlarını döndürür */
export function filterActiveShareMembers<T extends UserPresenceFields>(
  share: readonly T[] | null | undefined
): T[] {
  if (!Array.isArray(share)) return [];
  return share.filter((member) => !isUserDeleted(member));
}

/** Entry share listesinden yalnızca aktif assignee e-postalarını çıkarır */
export function getActiveShareEmails(share: readonly unknown[] | null | undefined): string[] {
  return filterActiveShareMembers(share as UserPresenceFields[])
    .map((member) => {
      const raw = member as { email?: string | null };
      return typeof raw.email === "string" ? raw.email.trim() : "";
    })
    .filter((email) => email.length > 0);
}

/** E-posta listesinden share havuzunda silinmiş olanları çıkarır */
export function filterActiveShareEmailList(
  emails: string[],
  ...pools: (readonly unknown[] | null | undefined)[]
): string[] {
  return emails.filter((email) => {
    const member = findShareMemberByEmail(email, ...pools);
    return !member || !isUserDeleted(member);
  });
}
